/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { QSTypography } from '../../../../../shared/components/atoms';
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { customerDetailAtomFamily, listFieldSetter } from '../../states/customer-details';
import useQuery from '../../../../../shared/hooks/useQuery';
import Loader from '../../../../../shared/components/molecules/loader';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { chatFiltersSetter, chatListAtom } from '../../states/chat-list';
import {
  ChatListReducerActionTypes,
  GetSingleSelectCustomField,
  CustomFieldsData,
  GetAllCustomFieldsLeadStatusProps,
  LeadStatusListProps,
} from '../../typings';
import { getAllCustomFields } from '../../requests/get-custom-fields';
import { customFieldsAtom, refreshLeadsAtom } from '../../states/custom-fields';
import { HEADER_HEIGHT } from '../../../../../shared/typings';
import { getAllCustomFieldsLeadStatus } from '../../requests/get-custom-fields-lead-status';
import { createRequestAtom } from '../../../../../shared/utils/request-atom';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { ButtonBase } from '@mui/material';
import CustomListDropdown from './custom-list-dropdown';
import { updateLeadCustomField } from '../../requests/lead-custom-field';
import useNotifications from '../../../../../shared/hooks/use-notifications';
import { selectedChatAtom } from '../../states/selected-chat';
import ArrowExpandIcon from '../../../../../shared/icons/icons-revamp/arrow_expand';
import { ChatType } from '../chat-list-section/chat-row/typings';

const classes = {
  container: css`
    height: ${HEADER_HEIGHT}px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border-bottom: 1px solid rgba(224, 224, 234, 1);
    background: white;
  `,
  filtersContainer: css`
    background: white;
    border-radius: 4px;
    border: 1px solid #d1d1d1;
    // padding: 4px;
    overflow: auto;
    display: flex;
    align-items: center;
    position: relative;

    &::-webkit-scrollbar {
      display: none;
    }

    & {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    & > div:not(:last-child) {
      // margin-right: 8px;
    }
  `,
  filter: css`
    // border: 1px solid transparent;
    padding: 18px 12px;
    height: 30px;
    display: flex;
    align-items: center;
    min-width: max-content;
    justify-content: center;
    // border-radius: 4px;
    color: #545454;

    &:hover {
      background: #98989825;
      cursor: pointer;
    }
  `,
  active: css`
    background: #dfede3 !important;
    color: #0d7341;
    border-color: #117340;

    &:hover {
      cursor: not-allowed;
    }

    span {
      background: #3d8d63;
      color: white;
    }
  `,
  filterText: css`
    font-size: 13px;
    color: inherit;
    font-weight: 700;
  `,
  disabled: css`
    opacity: 0.5;
    pointer-events: none;
  `,
  filterIcon: css`
    color: inherit;
    background: #dbdbdb;
    font-size: 11px;
    font-weight: 700;
    margin-left: 6px;
    border-radius: 4px;
    padding: 2px 8px;
  `,
};
const requestAtom = createRequestAtom<GetAllCustomFieldsLeadStatusProps>();

const LeadStagesFilters = () => {
  const {
    currentCustomListId,
    status,
    leadCustomFieldIdList,
    searchTerm,
    waba,
    assignedUserId,
    slaStatus,
    tags,
    tagIds,
    customFieldConditions,
  } = useAtomValue(chatListAtom);
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [updatingLeadCustomField, setupdatingLeadCustomField] = useState(false);
  const { addNotification } = useNotifications();
  const [isScrollableLeft, setIsScrollableLeft] = useState(false);
  const [isScrollableRight, setIsScrollableRight] = useState(false);
  const isScrollable = isScrollableLeft || isScrollableRight;
  const selectedChat = useAtomValue(selectedChatAtom);

  const phone = selectedChat?.chatType === ChatType.INDIVIDUAL ? selectedChat.phoneNumber : '';
  const integrationWabaNumber = selectedChat?.integrationWabaNumber || '';

  const customerDetailAtom = useMemo(() => {
    return customerDetailAtomFamily({
      id: `${integrationWabaNumber}::${phone}`,
    });
  }, [integrationWabaNumber, phone]);

  const { data: customerDetails } = useAtomValue(customerDetailAtom);

  const setFilters = useSetAtom(chatFiltersSetter);
  const setRefreshLeads = useSetAtom(refreshLeadsAtom);

  const { data: allCustomFieldsData } = useAtomValue(customFieldsAtom);

  const handleLeadCustomFieldChange = async (updatedField: GetSingleSelectCustomField) => {
    try {
      setupdatingLeadCustomField(true);
      await updateLeadCustomField(updatedField.id);
      await refreshData();
      setFilters({
        type: ChatListReducerActionTypes.CURRENT_CUSTOM_LIST_ID,
        data: updatedField.id,
      });
      setFilters({
        type: ChatListReducerActionTypes.LEAD_CUSTOM_FIELD_LIST,
        data: {
          listId: updatedField.id,
          fieldId: '',
        },
      });
      addNotification({
        type: 'success',
        message: 'List filter setting updated successfully',
      });

      // Trigger lead-status refresh
      setRefreshLeads((prev) => prev + 1);
    } catch (error) {
      addNotification({
        type: 'error',
        message: (error as Error)?.message || 'Failed to update list filter setting. Please try again later.',
      });
    } finally {
      setupdatingLeadCustomField(false);
    }
  };

  const wabaString = useMemo(() => JSON.stringify(waba), [waba]);

  const getter = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    const data = await getAllCustomFieldsLeadStatus(
      status,
      searchTerm,
      assignedUserId,
      waba,
      slaStatus,
      tags,
      tagIds,
      signal,
      customFieldConditions
    );

    setFilters({
      type: ChatListReducerActionTypes.CURRENT_CUSTOM_LIST_ID,
      data: data?.customField?.id,
    });

    return data;
  }, [
    status,
    searchTerm,
    assignedUserId,
    wabaString,
    slaStatus,
    tags,
    tagIds,
    allCustomFieldsData,
    customFieldConditions,
  ]);

  const {
    requestData: { data: filteredListTagData, loading },
    refreshData,
  } = useQuery<GetAllCustomFieldsLeadStatusProps>({
    queryFunction: getter,
    requestAtom: requestAtom,
  });

  const SCROLL_AMOUNT = 250;

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      setIsScrollableLeft(scrollLeft > 0);

      setIsScrollableRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollability();
  }, [filteredListTagData?.customField?.list]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollability);
      }
    };
  }, []);

  const handleLeftArrowClick = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -SCROLL_AMOUNT,
        behavior: 'smooth',
      });
    }
  };

  const handleRightArrowClick = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: SCROLL_AMOUNT,
        behavior: 'smooth',
      });
    }
  };

  const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      setFilters({
        type: ChatListReducerActionTypes.LEAD_CUSTOM_FIELD_LIST,
        data: {
          listId: currentCustomListId,
          fieldId: event.currentTarget.id,
        },
      });
    },
    [setFilters, currentCustomListId]
  );

  const singleSelectFields =
    (allCustomFieldsData?.customFields ?? []).filter((field) => field.type === 'single_select') || [];

  const selectedListField = useMemo(() => {
    const listExists = singleSelectFields.find(
      (field) => field.id === currentCustomListId
    ) as GetSingleSelectCustomField;
    return listExists ?? (singleSelectFields?.[0] as GetSingleSelectCustomField);
  }, [currentCustomListId, singleSelectFields]);

  const currentListFilter = leadCustomFieldIdList?.listId == currentCustomListId ? leadCustomFieldIdList?.fieldId : '';
  const isAllSelected = currentListFilter === '';
  const showDropdownOption = singleSelectFields.length > 1;

  const refreshLeads = useAtomValue(refreshLeadsAtom);

  useEffect(() => {
    if (refreshLeads > 0) {
      refreshData();
    }
  }, [refreshLeads, refreshData]);

  const renderRightContent = () => {
    if (updatingLeadCustomField) {
      return (
        <div
          css={css`
            display: flex;
            padding: 4px 8px;
            align-items: center;
            border-left: 1px solid #e0e0e9;
            background: #fff;
            overflow: hidden;
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          `}
        >
          <Loader size={18} />
        </div>
      );
    }

    if (singleSelectFields.length > 1) {
      return (
        <CustomListDropdown selectedListField={selectedListField} setSelectedListField={handleLeadCustomFieldChange} />
      );
    }

    return null;
  };

  const getPaddingRight = () => {
    return isScrollable ? (showDropdownOption ? 78 : 40) : showDropdownOption ? 37 : 4;
  };

  const getPaddingLeft = () => {
    return isScrollable ? 41 : 0;
  };

  return (
    <div css={classes.container}>
      <div
        css={css`
          position: relative;
          display: flex;
          overflow: hidden;
        `}
      >
        {!loading && isScrollable && (
          <ButtonBase
            css={css`
              position: absolute;
              top: 1px;
              left: 1px;
              bottom: 1px;
              height: 100%;
              border-top-left-radius: 7px;
              border-bottom-left-radius: 7px;
              max-height: calc(100% - 2px);
              z-index: 1;
              display: flex;
              padding: 4px 6px;
              align-items: center;
              background: #fff;
              border-right: 1px solid #e0e0e9;
              overflow: hidden;
              box-shadow: ${isScrollableLeft ? '8px 0px 14px rgba(0, 0, 0, 0.12)' : 'none'};
              svg {
                transform: rotate(90deg);
              }
              &:hover {
                path {
                  stroke: #2d2d2d;
                }
              }
            `}
            onClick={handleLeftArrowClick}
          >
            <ArrowExpandIcon color="#5f6368" size={28} />
          </ButtonBase>
        )}
        <div
          css={[
            classes.filtersContainer,
            css`
              padding-right: ${getPaddingRight()}px;
              padding-left: ${getPaddingLeft()}px;
            `,
          ]}
          ref={scrollContainerRef}
        >
          <div css={css(classes.filter, isAllSelected && classes.active)} onClick={onClick}>
            <QSTypography css={classes.filterText}>All</QSTypography>
          </div>
          {(filteredListTagData?.customField?.list ?? []).map((stage: LeadStatusListProps, index) => {
            const isSelected = currentListFilter === stage.id;

            return (
              <div
                key={index}
                css={css(classes.filter, isSelected && classes.active, loading && classes.disabled)}
                id={stage.id}
                onClick={onClick}
              >
                <QSTypography css={classes.filterText}>{stage.name}</QSTypography>
                {!!stage.customerCount && (
                  <QSTypography variant="caption" css={classes.filterIcon}>
                    {stage.customerCount}
                  </QSTypography>
                )}
              </div>
            );
          })}
        </div>
        {!loading ? (
          <div
            css={css`
              position: absolute;
              top: 1px;
              right: 1px;
              bottom: 1px;
              display: flex;
              height: 100%;
              max-height: calc(100% - 2px);
              background: white;
              border-radius: 8px;
            `}
          >
            {isScrollable ? (
              <ButtonBase
                css={css`
                  display: flex;
                  padding: 4px 6px;
                  align-items: center;
                  border-left: 1px solid #e0e0e9;
                  background: #fff;
                  overflow: hidden;
                  box-shadow: ${isScrollableRight ? '-8px 0px 14px rgba(0, 0, 0, 0.12)' : 'none'};
                  border-top-right-radius: ${!showDropdownOption ? '8px' : 'unset'};
                  border-bottom-right-radius: ${!showDropdownOption ? '8px' : 'unset'};
                  svg {
                    transform: rotate(-90deg);
                  }
                  &:hover {
                    path {
                      stroke: #2d2d2d;
                    }
                  }
                `}
                onClick={handleRightArrowClick}
              >
                <ArrowExpandIcon color="#5f6368" size={28} />
              </ButtonBase>
            ) : null}
            {renderRightContent()}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const LeadStagesFiltersMiddleware = () => {
  const { currentCustomListId } = useAtomValue(chatListAtom);
  const setFilters = useSetAtom(chatFiltersSetter);
  const setListFieldValues = useSetAtom(listFieldSetter);
  const {
    requestData: { data, loading, error },
  } = useQuery<CustomFieldsData | undefined>({
    requestAtom: customFieldsAtom,
    queryFunction: getAllCustomFields,
  });

  useEffect(() => {
    if (!loading && data) {
      const singleSelectFields = (data?.customFields ?? []).filter((field) => field.type === 'single_select') || [];
      setListFieldValues(singleSelectFields as GetSingleSelectCustomField[]);
      if (singleSelectFields && singleSelectFields[0] && singleSelectFields[0]) {
        setFilters({
          type: ChatListReducerActionTypes.CURRENT_CUSTOM_LIST_ID,
          data: singleSelectFields[0].id,
        });
      }
    }
  }, [loading, data]);

  if (loading || error) {
    return (
      <div css={classes.container}>
        <div
          css={css`
            width: 100%;
            display: flex;
            align-items: center;
          `}
        >
          <Loader size={18} />
          <QSTypography
            css={css`
              margin-left: 8px;
              font-size: 13px;
            `}
          >
            Loading lead stages...
          </QSTypography>
        </div>
      </div>
    );
  }

  if (!currentCustomListId) {
    return null;
  }

  return <LeadStagesFilters />;
};

export default LeadStagesFiltersMiddleware;
